// Shared brand-color palette generation utilities.
// Used by AdminPage, TeacherDashboardPage, DashboardPage (parent).

export function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h * 360, s, l];
}

export function hslToHex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  const toHex = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Generate 4 visually distinct color sets from brand primary.
 *
 * Between cards: 90° hue separation → each card is clearly different.
 * Within a card: rings use complementary hues (180° apart) → max contrast
 *   between outer and inner ring. 3rd ring uses triadic (120°).
 */
export function generateDonutPalette(primary: string, secondary: string) {
  const [h1, s1, l1] = hexToHsl(primary);
  const [, s2, l2] = hexToHsl(secondary);

  const baseSat = Math.max(0.65, Math.min(0.90, Math.max(s1, s2) + 0.15));
  const baseLit = Math.max(0.45, Math.min(0.55, (l1 + l2) / 2 + 0.10));

  const cardHues = [h1, (h1 + 90) % 360, (h1 + 180) % 360, (h1 + 270) % 360];

  return cardHues.map(h => ({
    bold:  hslToHex(h,              baseSat,        baseLit),
    muted: hslToHex((h + 180) % 360, baseSat * 0.85, baseLit + 0.05),
    third: hslToHex((h + 120) % 360, baseSat * 0.75, baseLit + 0.10),
  }));
}

/**
 * Generate an extended chart palette (10+ distinct colors) harmonious with brand.
 */
export function generateChartPalette(primary: string, secondary: string): string[] {
  const [h1, s1, l1] = hexToHsl(primary);
  const [, s2, l2] = hexToHsl(secondary);

  const baseSat = Math.max(0.55, Math.min(0.80, (s1 + s2) / 2 + 0.10));
  const baseLit = Math.max(0.42, Math.min(0.55, (l1 + l2) / 2 + 0.08));

  return [primary, secondary, ...Array.from({ length: 9 }, (_, i) => {
    const hue = (h1 + 36 * (i + 1)) % 360;
    const sat = baseSat + (i % 2 === 0 ? 0.06 : -0.06);
    const lit = baseLit + (i % 3 === 0 ? 0.05 : i % 3 === 1 ? -0.04 : 0);
    return hslToHex(hue, sat, lit);
  })];
}

// Day-of-week mapping to JS getDay() values (0=Sun … 6=Sat), multi-language
export const DAY_MAP: Record<string, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
  неделя: 0, понеделник: 1, вторник: 2, сряда: 3, четвъртък: 4, петък: 5, събота: 6,
  sonntag: 0, montag: 1, dienstag: 2, mittwoch: 3, donnerstag: 4, freitag: 5, samstag: 6,
};

export function getDatesForDayOfWeek(dayOfWeek: string, year: number, month: number): string[] {
  const jsDay = DAY_MAP[dayOfWeek.toLowerCase()];
  if (jsDay === undefined) return [];
  const dates: string[] = [];
  const d = new Date(year, month, 1);
  while (d.getDay() !== jsDay) d.setDate(d.getDate() + 1);
  while (d.getMonth() === month) {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    dates.push(`${d.getFullYear()}-${m}-${day}`);
    d.setDate(d.getDate() + 7);
  }
  return dates;
}
